'use client';

import React, { useState, useEffect } from 'react';
import { discardHTMLTags, getCurrencySymbol, getBaseImageUrl, calculateOfferDuration } from '@/constants/hooks';
import { Offer } from '@/services/dataTypes';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { apiAddComment } from '@/apis/offers';
import { toast } from 'react-toastify';
import {
    FontAwesomeIcon,
    faCheck,
    faCalendarDays,
    faThumbsDown,
    faThumbsUp,
    faTimes,
    faCopy,
    filledStar,
    emptyStar,
    faPaperPlane,
    faBolt,
    faFire,
    faArrowRightLong,
    faStore
} from '@/constants/icons';

interface ProductCommentModalProps {
    product: Offer;
    merchantName: string;
    merchantLogo: string;
    domain: string;
    isOpen: boolean;
    onClose: () => void;
}

const ProductCommentModal = ({
    product,
    merchantName,
    merchantLogo,
    domain,
    isOpen,
    onClose
}: ProductCommentModalProps) => {
    const [mounted, setMounted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [dealLoading, setDealLoading] = useState(false);
    const [helpfulVote, setHelpfulVote] = useState<'yes' | 'no' | null>(null);

    // Comment & Rating Form state (starts at 0 unselected stars)
    const [rating, setRating] = useState<number>(0);
    const [hoverRating, setHoverRating] = useState<number>(0);
    const [commentText, setCommentText] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Mobile View Tab Switcher ('offer' | 'review')
    const [activeTab, setActiveTab] = useState<'offer' | 'review'>('offer');
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setIsExpanded(false);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Reset transients on opening new product
    useEffect(() => {
        if (isOpen && product?.unique_id) {
            setCopied(false);
            setDealLoading(false);
            setHelpfulVote(null);
            setRating(0);
            setHoverRating(0);
            setCommentText('');
            setActiveTab('offer');
            setIsExpanded(false);

            const lastRated = localStorage.getItem(`hasRated_${product.unique_id}`);
            setHasSubmitted(!!(lastRated && new Date().getTime() - parseInt(lastRated, 10) < 86400000));
        }
    }, [isOpen, product?.unique_id]);

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return;
        try {
            await navigator.clipboard.writeText(product.coupon_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            try {
                const el = document.createElement('textarea');
                el.value = product.coupon_code;
                el.style.position = 'fixed';
                el.style.opacity = '0';
                document.body.appendChild(el);
                el.focus();
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
            } catch {
                toast.error('Failed to copy. Please copy manually.', { autoClose: 3000 });
            }
        }
    };

    const handleHelpful = (type: 'yes' | 'no') => {
        setHelpfulVote(type);
        toast.success(type === 'yes' ? 'Thanks! Marked as working.' : 'Feedback recorded.', { autoClose: 2000 });
    };

    const handleSubmitComment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rating && !commentText.trim()) {
            toast.error('Please select a star rating or enter a comment.', { autoClose: 2000 });
            return;
        }

        setSubmitting(true);
        try {
            const offerId = product?.unique_id?.toString() || '';
            const companyId = product?.company_id?.toString() || '';
            if (offerId && companyId) {
                await apiAddComment(offerId, companyId, commentText, rating.toString());
            }
            if (offerId) {
                localStorage.setItem(`hasRated_${offerId}`, new Date().getTime().toString());
            }
            setHasSubmitted(true);
            setCommentText('');
            setRating(0);
            toast.success('Thank you for your feedback!', { autoClose: 2500 });
        } catch {
            toast.error('Could not submit review. Please try again.', { autoClose: 2000 });
        } finally {
            setSubmitting(false);
        }
    };

    const finalUrl = product?.url?.startsWith('/') ? product.url.replace(/^\/+/, '') : (product?.url ?? '');
    const absoluteOutUrl = `/${finalUrl}`;
    const imageSrc = (product?.offer_type?.name === 'product' && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '')
        : getBaseImageUrl(domain, merchantLogo, '');

    const hasCoupon = Boolean(product?.coupon_code);
    const isLifetime = !product?.end_date || product?.end_date?.toLowerCase().includes('lifetime') || product?.end_date?.toLowerCase().includes('life time') || product?.end_date?.toLowerCase().includes('no expiry');
    const offerDuration = product?.end_date && !isLifetime ? calculateOfferDuration(product?.end_date) : null;

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0;
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0;
    const discountPercent = (originalPrice > 0 && salePrice > 0 && originalPrice > salePrice)
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
        : null;

    const modalStyles = `
        @keyframes modalFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes modalPopUp {
            from {
                opacity: 0;
                transform: scale(0.96) translateY(12px);
            }
            to {
                opacity: 1;
                transform: scale(1) translateY(0);
            }
        }
        .modal-custom-scroll::-webkit-scrollbar {
            display: none;
        }
        .modal-custom-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
    `;

    const modalContent = isOpen ? (
        <>
            <style dangerouslySetInnerHTML={{ __html: modalStyles }} />
            <div
                className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6 overflow-y-auto"
                style={{ animation: 'modalFadeIn 0.2s ease-out forwards', willChange: 'transform', isolation: 'isolate', transform: 'translateZ(0)' }}
            >
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-slate-950/60 transition-opacity duration-300"
                    onClick={onClose}
                />

                {/* Main Modal Window (Bottom Sheet on Mobile, Centered Modal on Desktop) */}
                <div
                    className="relative w-full max-w-4xl bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[90vh] z-10 border-t sm:border border-slate-100"
                    style={{ animation: 'modalPopUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Mobile Sheet Drag Indicator Handle */}
                    <div className="sm:hidden pt-3 pb-1 flex justify-center bg-white shrink-0">
                        <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
                    </div>

                    {/* Floating Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-3.5 right-3.5 sm:top-5 sm:right-5 z-30 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 hover:bg-rose-50 text-slate-400 hover:text-rose-600 border border-slate-200 hover:border-rose-200 flex items-center justify-center transition-all duration-200 shadow-sm shrink-0"
                        aria-label="Close modal"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>

                    {/* ─── SCROLLABLE CONTENT AREA ─── */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 modal-custom-scroll bg-white">

                        {/* Mobile Segmented Tab Switcher (Visible on < lg screens) */}
                        <div className="lg:hidden flex bg-slate-100/90 p-1.5 rounded-2xl mb-4 border border-slate-200/60 shadow-inner">
                            <button
                                type="button"
                                onClick={() => setActiveTab('offer')}
                                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'offer'
                                    ? 'bg-white text-indigo-600 shadow-md shadow-indigo-500/10'
                                    : 'text-slate-500 hover:text-slate-900'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 text-indigo-500" />
                                Offer Details
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('review')}
                                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 ${activeTab === 'review'
                                    ? 'bg-white text-indigo-600 shadow-md shadow-indigo-500/10'
                                    : 'text-slate-500 hover:text-slate-900'
                                    }`}
                            >
                                <FontAwesomeIcon icon={filledStar} className="w-3.5 h-3.5 text-amber-400" />
                                Rate & Review
                                {hasSubmitted && (
                                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                )}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8 items-start pt-1">

                            {/* 👈 LEFT COLUMN: Offer Details & Action (7 cols) */}
                            <div className={`lg:col-span-7 space-y-4 sm:space-y-5 ${activeTab === 'offer' ? 'block' : 'hidden lg:block'}`}>

                                {/* Header Section: Only % OFF & Store Name upside title, Logo + Title, Verified/Duration below title, Description */}
                                <div className="space-y-3 sm:space-y-3.5">

                                    {/* Upper row: Store Name & % OFF tag only */}
                                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pr-8 sm:pr-10">
                                        <span className="text-[10px] sm:text-xs font-black text-indigo-600 bg-indigo-50/80 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg border border-indigo-100">
                                            {merchantName}
                                        </span>

                                        {Boolean(discountPercent && discountPercent > 0) && (
                                            <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-black bg-rose-500 text-white uppercase tracking-wider shadow-sm">
                                                <FontAwesomeIcon icon={faFire} className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                                                {discountPercent}% OFF
                                            </span>
                                        )}
                                    </div>

                                    {/* Logo + Main Heading + Badges below title */}
                                    <div className="flex items-start gap-3 sm:gap-4">
                                        {imageSrc ? (
                                            <div className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-white p-1.5 sm:p-2 border border-slate-200 shadow-md flex items-center justify-center shrink-0 overflow-hidden">
                                                <Image
                                                    src={imageSrc}
                                                    alt={merchantName || 'Brand'}
                                                    fill
                                                    className="object-contain p-1.5 sm:p-2"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                                                <FontAwesomeIcon icon={faStore} className="w-5 h-5 sm:w-6 sm:h-6" />
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0 pr-4 sm:pr-6 pt-0.5 space-y-1.5 sm:space-y-2">
                                            <h2 className="text-base sm:text-lg lg:text-xl font-extrabold text-slate-900 leading-snug">
                                                {discardHTMLTags(product?.offer_title)}
                                            </h2>

                                            {/* Badges row below the title */}
                                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                                <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider">
                                                    <FontAwesomeIcon icon={faCheck} className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-600" />
                                                    Verified
                                                </span>
                                                {offerDuration && (
                                                    <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider">
                                                        <FontAwesomeIcon icon={faCalendarDays} className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-500" />
                                                        {offerDuration}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description directly below the title */}
                                    {product?.offer_detail && (() => {
                                        const cleanDetail = discardHTMLTags(product.offer_detail);
                                        const isLong = cleanDetail.length > 150;
                                        return (
                                            <div className="space-y-1.5 pt-1">
                                                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    Description
                                                </h4>
                                                <div className="p-3.5 sm:p-5 bg-slate-50/80 rounded-xl sm:rounded-2xl border border-slate-100 text-slate-600 text-xs sm:text-sm leading-relaxed">
                                                    <p className={`text-slate-600 text-xs sm:text-sm leading-relaxed ${isLong && !isExpanded ? 'line-clamp-2 sm:line-clamp-3' : ''}`}>
                                                        {cleanDetail}
                                                    </p>
                                                    {isLong && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsExpanded(!isExpanded)}
                                                            className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors cursor-pointer group"
                                                        >
                                                            <span>{isExpanded ? 'See Less' : 'Read More'}</span>
                                                            <svg
                                                                className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Compact Price Pill */}
                                {(product?.sale_price || product?.original_price) && (
                                    <div className="inline-flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-1.5 sm:py-2 bg-indigo-50/80 hover:bg-indigo-100/70 border border-indigo-100 hover:border-indigo-200 rounded-xl w-fit shadow-2xs transition-all duration-200 cursor-default">
                                        <span className="text-[9px] sm:text-[10px] font-black text-indigo-400 uppercase tracking-widest">Price</span>
                                        <div className="flex items-baseline gap-1.5 sm:gap-2">
                                            {product?.sale_price && (
                                                <span className="text-lg sm:text-2xl font-black text-indigo-700 leading-none">
                                                    {getCurrencySymbol(product?.currency)}{product.sale_price}
                                                </span>
                                            )}
                                            {product?.original_price && originalPrice > salePrice && (
                                                <span className="text-[10px] sm:text-xs line-through text-slate-400 font-semibold leading-none">
                                                    {getCurrencySymbol(product?.currency)}{product.original_price}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Coupon Code Block */}
                                {hasCoupon ? (
                                    <div className="p-4 sm:p-5 bg-gradient-to-br from-indigo-50/60 via-purple-50/30 to-slate-50 border-2 border-dashed border-indigo-200 rounded-xl sm:rounded-2xl space-y-2.5 sm:space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                Coupon Code
                                            </span>
                                            <span className="text-[9px] sm:text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                                                Copy & Apply at checkout
                                            </span>
                                        </div>

                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3 bg-white p-2.5 sm:p-3 rounded-xl border border-slate-200 shadow-sm">
                                            <span className="font-mono text-lg sm:text-2xl font-black text-indigo-700 tracking-wider sm:tracking-widest select-all px-1 sm:px-2 text-center sm:text-left break-all sm:break-normal">
                                                {product.coupon_code}
                                            </span>
                                            <button
                                                onClick={handleCopyCode}
                                                className={`w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 shadow-sm ${copied
                                                    ? 'bg-emerald-600 text-white shadow-emerald-500/20 scale-105'
                                                    : 'bg-indigo-600 hover:bg-slate-900 text-white active:scale-95'
                                                    }`}
                                            >
                                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="w-3.5 h-3.5" />
                                                {copied ? 'Copied!' : 'Copy Code'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-3.5 sm:p-4 bg-emerald-50/70 border border-emerald-200/80 rounded-xl sm:rounded-2xl flex items-center gap-2.5 sm:gap-3 text-emerald-800 text-xs sm:text-sm font-bold">
                                        <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 shrink-0" />
                                        No promo code required! Discount is automatically applied when you visit the merchant.
                                    </div>
                                )}

                                {/* Direct Shop Redirect CTA */}
                                <button
                                    onClick={() => {
                                        setDealLoading(true);
                                        setTimeout(() => {
                                            window.open(absoluteOutUrl, '_blank', 'noopener,noreferrer');
                                            setDealLoading(false);
                                        }, 600);
                                    }}
                                    disabled={dealLoading}
                                    className="w-full py-3.5 sm:py-4 px-4 sm:px-6 bg-indigo-600 hover:bg-slate-900 disabled:opacity-50 text-white text-xs sm:text-sm font-extrabold uppercase tracking-widest rounded-xl sm:rounded-2xl transition-all duration-200 shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2.5 sm:gap-3 active:scale-[0.99]"
                                >
                                    {dealLoading ? (
                                        <>
                                            <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                            Opening Merchant Site...
                                        </>
                                    ) : (
                                        <>
                                            Visit Store & Redeem Offer
                                            <FontAwesomeIcon icon={faArrowRightLong} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* 👉 RIGHT COLUMN: Dedicated Review & Rating Card (5 cols) */}
                            <div className={`lg:col-span-5 bg-slate-50/70 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 space-y-3.5 sm:space-y-4 shadow-sm h-full flex flex-col justify-between ${activeTab === 'review' ? 'block' : 'hidden lg:block'}`}>
                                <div className="space-y-3.5 sm:space-y-4">
                                    <div className="flex items-center justify-between border-b border-slate-200/80 pb-2.5 sm:pb-3">
                                        <h4 className="text-xs sm:text-sm font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                                            <FontAwesomeIcon icon={filledStar} className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />
                                            Rate & Review
                                        </h4>
                                        {hasSubmitted && (
                                            <span className="text-[9px] sm:text-[10px] font-bold text-emerald-700 bg-emerald-100/70 px-2 sm:px-2.5 py-0.5 rounded-full border border-emerald-200">
                                                Submitted
                                            </span>
                                        )}
                                    </div>

                                    <form onSubmit={handleSubmitComment} className="space-y-3.5 sm:space-y-4">
                                        {/* Star Rating Picker: Starts white/slate, turns yellow on hover or selection */}
                                        <div className="space-y-1 sm:space-y-1.5 bg-white p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200/80 text-center shadow-2xs">
                                            <span className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-wider block">
                                                Select Your Rating
                                            </span>
                                            <div className="flex items-center justify-center gap-1 sm:gap-1.5 py-1 sm:py-1.5" onMouseLeave={() => setHoverRating(0)}>
                                                {[1, 2, 3, 4, 5].map((starVal) => {
                                                    const active = starVal <= (hoverRating || rating);
                                                    return (
                                                        <button
                                                            key={starVal}
                                                            type="button"
                                                            onClick={() => setRating(starVal)}
                                                            onMouseEnter={() => setHoverRating(starVal)}
                                                            className="p-1 focus:outline-none transition-transform hover:scale-125"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={active ? filledStar : emptyStar}
                                                                className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors duration-150 ${active
                                                                    ? 'text-amber-400 drop-shadow-sm'
                                                                    : 'text-slate-300'
                                                                    }`}
                                                            />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Review Comment Textarea */}
                                        <div className="space-y-1 sm:space-y-1.5">
                                            <label className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                                                Feedback & Comment
                                            </label>
                                            <textarea
                                                rows={3}
                                                placeholder="Did the code work for you? Share your experience..."
                                                value={commentText}
                                                onChange={(e) => setCommentText(e.target.value)}
                                                className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 bg-white text-slate-800 placeholder-slate-400 text-xs font-medium rounded-xl border border-slate-200 focus:border-indigo-500 focus:outline-none outline-none resize-none shadow-sm"
                                            />
                                        </div>

                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full py-3 sm:py-3.5 px-4 bg-slate-900 hover:bg-indigo-600 disabled:opacity-50 text-white text-xs font-extrabold uppercase tracking-widest rounded-xl transition-all duration-200 shadow-md flex items-center justify-center gap-2 active:scale-95"
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faPaperPlane} className="w-3.5 h-3.5" />
                                                    Submit Review
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* ─── FOOTER BAR (Helpful Voting) ─── */}
                    <div className="px-4 py-3 sm:px-8 bg-slate-50/90 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3 shrink-0">
                        <span className="text-[11px] sm:text-xs font-bold text-slate-500">
                            Did this coupon work for you?
                        </span>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button
                                onClick={() => handleHelpful('yes')}
                                className={`flex-1 sm:flex-none justify-center px-4 py-2 rounded-xl text-xs font-extrabold flex items-center gap-2 border transition-all duration-200 cursor-pointer ${helpfulVote === 'yes'
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-500/30 scale-105'
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500 hover:border-emerald-600 shadow-sm hover:shadow-md hover:shadow-emerald-500/25 hover:scale-[1.02] active:scale-95'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faThumbsUp} className="w-3.5 h-3.5" />
                                Yes, Worked
                            </button>
                            <button
                                onClick={() => handleHelpful('no')}
                                className={`flex-1 sm:flex-none justify-center px-4 py-2 rounded-xl text-xs font-extrabold flex items-center gap-2 border transition-all duration-200 cursor-pointer ${helpfulVote === 'no'
                                    ? 'bg-rose-600 text-white border-rose-600 shadow-md shadow-rose-500/30 scale-105'
                                    : 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500 hover:border-rose-600 shadow-sm hover:shadow-md hover:shadow-rose-500/25 hover:scale-[1.02] active:scale-95'
                                    }`}
                            >
                                <FontAwesomeIcon icon={faThumbsDown} className="w-3.5 h-3.5" />
                                No
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    ) : null;

    if (!mounted || typeof window === 'undefined' || !modalContent || !document?.body) return null;

    return createPortal(modalContent, document.body);
};

export default ProductCommentModal;